import React, { useState, useEffect, useCallback } from 'react';

const BASE_URL =
  process.env.NODE_ENV === 'production'
    ? 'https://api.ruqqiasultanaclinic.com'
    : '';

const API_TOKEN =
  'A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2';

const AUTH = { accept: '*/*', 'X-API-TOKEN': API_TOKEN };

// ─── PDF Utils ────────────────────────────────────────────────
// Chunked base64 decode — handles any size PDF without stack overflow

const base64ToBlob = (base64) => {
  const raw    = base64.includes(',') ? base64.split(',')[1] : base64;
  const clean  = raw.replace(/[\s\r\n]/g, '');
  const padded = clean + '='.repeat((4 - (clean.length % 4)) % 4);

  const CHUNK = 8192;
  const parts = [];

  for (let i = 0; i < padded.length; i += CHUNK) {
    const slice  = padded.slice(i, i + CHUNK);
    const binary = atob(slice);
    const bytes  = new Uint8Array(binary.length);
    for (let j = 0; j < binary.length; j++) bytes[j] = binary.charCodeAt(j);
    parts.push(bytes);
  }

  return new Blob(parts, { type: 'application/pdf' });
};

const openPDF = (base64) => {
  const blob = base64ToBlob(base64);
  const url  = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

const downloadPDF = (base64, filename) => {
  const blob = base64ToBlob(base64);
  const url  = URL.createObjectURL(blob);
  const a    = Object.assign(document.createElement('a'), {
    href: url, download: filename || 'report.pdf',
  });
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5_000);
};

// ─── API ──────────────────────────────────────────────────────

const api = {
  getPatients: async () => {
    const r = await fetch(`${BASE_URL}/api/Patient/patientdropdown/get`, { headers: AUTH });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  },

  // Get all reports for table (metadata only — base64 may be large)
  getAllReports: async () => {
    const r = await fetch(`${BASE_URL}/api/Patient/GetLabReport?PatientId=0`, { headers: AUTH });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  },

  // ✅ Fetch by specific patientId — returns COMPLETE base64 for that patient only
  // This avoids the truncation that happens when fetching ALL reports at once
  getReportsByPatient: async (patientId) => {
    const r = await fetch(`${BASE_URL}/api/Patient/GetLabReport?PatientId=${patientId}`, { headers: AUTH });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  },

  // Save base64 to DB
  saveReport: async (payload) => {
    const r = await fetch(`${BASE_URL}/api/Patient/MedicalReportCreateUpdate`, {
      method:  'POST',
      headers: { ...AUTH, 'Content-Type': 'application/json' },
      body:    JSON.stringify(payload),
    });
    if ([200, 201, 204].includes(r.status)) {
      const text = await r.text();
      return text ? JSON.parse(text) : {};
    }
    throw new Error(`Save failed: HTTP ${r.status}`);
  },
// External Lab APIs
  getAllExternalLabs: async () => {
    const r = await fetch(`${BASE_URL}/api/Patient/GetExternalLab/0/patientid/0`, { headers: AUTH });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  },

  getExternalLabById: async (id, patientId) => {
    const r = await fetch(`${BASE_URL}/api/Patient/GetExternalLab/${id}/patientid/${patientId}`, { headers: AUTH });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  },

  insertExternalLab: async (payload) => {
    const r = await fetch(`${BASE_URL}/api/Patient/InsertExternalLab`, {
      method: 'POST',
      headers: { ...AUTH, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  },

  updateExternalLab: async (payload) => {
    const r = await fetch(`${BASE_URL}/api/Patient/UpdateExternalLab`, {
      method: 'POST',
      headers: { ...AUTH, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  },

};

// ─── Constants ────────────────────────────────────────────────

const TEST_TYPES = [
  { id: 1,  name: 'Complete Blood Count (CBC)' },
  { id: 2,  name: 'Blood Sugar (Fasting)' },
  { id: 3,  name: 'Blood Sugar (Random)' },
  { id: 4,  name: 'HbA1c' },
  { id: 5,  name: 'Liver Function Test (LFT)' },
  { id: 6,  name: 'Renal Function Test (RFT)' },
  { id: 7,  name: 'Lipid Profile' },
  { id: 8,  name: 'Thyroid Profile' },
  { id: 9,  name: 'Urine Complete' },
  { id: 10, name: 'Urine Culture' },
  { id: 11, name: 'Hepatitis B (HBsAg)' },
  { id: 12, name: 'Hepatitis C (Anti-HCV)' },
  { id: 13, name: 'HIV Screening' },
  { id: 14, name: 'Blood Group & Rh' },
  { id: 15, name: 'Coagulation Profile' },
  { id: 16, name: 'Electrolytes' },
  { id: 17, name: 'Ultrasound Report' },
  { id: 18, name: 'X-Ray Report' },
  { id: 19, name: 'ECG Report' },
  { id: 0,  name: 'Other' },
];

const getTestName = (id) =>
  TEST_TYPES.find((t) => t.id === Number(id))?.name ?? `Test #${id}`;

// ─── ReportActions ────────────────────────────────────────────
// Fetches full base64 by patientId on click — avoids truncation

const ReportActions = ({ report, patientMR }) => {
  const [busy,  setBusy]  = useState(false);
  const [err,   setErr]   = useState('');

  const act = async (type) => {
    setBusy(true);
    setErr('');
    try {
      // Fetch reports for THIS patient only → gets complete base64 from DB
      const list = await api.getReportsByPatient(report.patientId);
      const full = Array.isArray(list) ? list.find(r => r.id === report.id) : null;

      if (!full)            throw new Error('Report not found.');
      if (!full.reportFile) throw new Error('No file data in this report.');

      const filename = `${getTestName(full.testId)}-${patientMR || 'report'}-ID${full.id}.pdf`;

      if (type === 'view')     openPDF(full.reportFile);
      if (type === 'download') downloadPDF(full.reportFile, filename);

    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="flex gap-1">
        <button onClick={() => act('view')} disabled={busy}
          className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-40 font-medium transition">
          {busy ? '…' : '👁 View'}
        </button>
        <button onClick={() => act('download')} disabled={busy}
          className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:opacity-40 font-medium transition">
          {busy ? '…' : '⬇ Download'}
        </button>
      </div>
      {err && <p className="text-xs text-red-500 mt-1">{err}</p>}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────
// ─── External Lab Form Modal ──────────────────────────────────

const EMPTY_EXT = {
  id: 0, patientId: '', patientMRNo: '', testDate: '', patientName: '',
  ageSex: '', hBsAg: '', hcv: '', bloodSugarRandom: '', bloodGroup: '',
  rhFactor: '', alT_SGPT: '', alp: '', bilirubinTotal: '', urea: '',
  creatinine: '', uricAcid: '', pT_ProthrombinTime: '', aptt: '',
  hemoglobin: '', totalWBCCount: '', plateletCount: '',
};

const ExternalLabModal = ({ initial, patients, onClose, onSaved }) => {
  const [form, setForm] = useState(initial || EMPTY_EXT);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.patientId && !form.patientName) { alert('Enter patient info.'); return; }
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const payload = { ...form, patientId: Number(form.patientId) || 0, createdOn: now, modifiedOn: now };
      if (isEdit) await api.updateExternalLab(payload);
      else        await api.insertExternalLab(payload);
      alert(isEdit ? 'Report updated!' : 'Report saved!');
      onSaved();
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 text-center";

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="bg-[#1a2e4a] text-white px-6 py-4 rounded-t-xl flex justify-between items-start">
          <div>
            <h2 className="font-bold text-lg">{isEdit ? 'Edit' : 'Add'} Lab Report</h2>
            <p className="text-xs text-blue-200">Fill in patient details and test results</p>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-300 text-2xl font-bold leading-none">×</button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-4">

          {/* Patient Info */}
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">Patient Information</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Patient ID / MR#</label>
                <select value={form.patientId} onChange={e => {
                  const p = patients.find(x => String(x.id) === e.target.value);
                  setForm(f => ({
                    ...f,
                    patientId: e.target.value,
                    patientName: p ? (p.name || p.patientName || '') : f.patientName,
                    patientMRNo: p ? (p.mrNo || '') : f.patientMRNo,
                  }));
                }} className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300">
                  <option value="">-- Select --</option>
                  {patients.map(p => <option key={p.id} value={p.id}>{p.name || p.patientName} {p.mrNo ? `(${p.mrNo})` : ''}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                <input value={form.testDate || ''} onChange={e => set('testDate', e.target.value)}
                  placeholder="23/05/2026" type="text"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Patient Name</label>
                <input value={form.patientName || ''} onChange={e => set('patientName', e.target.value)}
                  placeholder="Full name"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Age / Sex</label>
                <input value={form.ageSex || ''} onChange={e => set('ageSex', e.target.value)}
                  placeholder="e.g. 35 / M"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
              </div>
            </div>
          </div>

          {/* Test Table */}
          <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-[#1a2e4a] text-white">
                <th className="text-left px-4 py-2 text-xs font-semibold w-8">#</th>
                <th className="text-left px-4 py-2 text-xs font-semibold">TEST</th>
                <th className="text-center px-4 py-2 text-xs font-semibold w-36">RESULT</th>
                <th className="text-center px-4 py-2 text-xs font-semibold w-20">UNIT</th>
                <th className="text-center px-4 py-2 text-xs font-semibold w-28">REFERENCE</th>
              </tr>
            </thead>
            <tbody>
              {/* Section: Serology */}
              <tr className="bg-blue-50">
                <td colSpan={5} className="px-4 py-2 text-xs font-bold text-blue-800 uppercase tracking-wide">I. Serology / Immunology</td>
              </tr>
              <tr className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 text-gray-500 text-xs">1.</td>
                <td className="px-4 py-2 text-gray-700">HBsAg (Hepatitis B)</td>
                <td className="px-2 py-2"><input value={form.hBsAg || ''} onChange={e => set('hBsAg', e.target.value)} className={inputCls} placeholder="—" /></td>
                <td className="px-2 py-2 text-center text-xs text-gray-400">—</td>
                <td className="px-2 py-2 text-center text-xs text-gray-400"></td>
              </tr>
              <tr className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 text-gray-500 text-xs">2.</td>
                <td className="px-4 py-2 text-gray-700">HCV (Hepatitis C)</td>
                <td className="px-2 py-2"><input value={form.hcv || ''} onChange={e => set('hcv', e.target.value)} className={inputCls} placeholder="—" /></td>
                <td className="px-2 py-2 text-center text-xs text-gray-400">—</td>
                <td className="px-2 py-2 text-center text-xs text-gray-400"></td>
              </tr>

              {/* Section: Biochemistry */}
              <tr className="bg-blue-50">
                <td colSpan={5} className="px-4 py-2 text-xs font-bold text-blue-800 uppercase tracking-wide">II. Biochemistry</td>
              </tr>
              <tr className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 text-gray-500 text-xs">3.</td>
                <td className="px-4 py-2 text-gray-700">Blood Sugar (Random)</td>
                <td className="px-2 py-2"><input value={form.bloodSugarRandom || ''} onChange={e => set('bloodSugarRandom', e.target.value)} className={inputCls} placeholder="—" /></td>
                <td className="px-2 py-2 text-center text-xs text-gray-500">mg/dL</td>
                <td className="px-2 py-2 text-center text-xs text-gray-400">80 - 160</td>
              </tr>
              <tr className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 text-gray-500 text-xs">4.</td>
                <td className="px-4 py-2 text-gray-700">Blood Group & Rh Factor</td>
                <td className="px-2 py-2">
                  <div className="flex gap-1">
                    <input value={form.bloodGroup || ''} onChange={e => set('bloodGroup', e.target.value)} className={inputCls} placeholder="BG" />
                    <input value={form.rhFactor || ''} onChange={e => set('rhFactor', e.target.value)} className={inputCls} placeholder="Rh" />
                  </div>
                </td>
                <td className="px-2 py-2 text-center text-xs text-gray-400">—</td>
                <td className="px-2 py-2 text-center text-xs text-gray-400"></td>
              </tr>

              {/* Section: LFTs */}
              <tr className="bg-blue-50">
                <td colSpan={5} className="px-4 py-2 text-xs font-bold text-blue-800 uppercase tracking-wide">5. LFTs (Liver Function Test)</td>
              </tr>
              <tr className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 text-gray-500 text-xs"></td>
                <td className="px-4 py-2 text-gray-600 pl-8">ALT (SGPT)</td>
                <td className="px-2 py-2"><input value={form.alT_SGPT || ''} onChange={e => set('alT_SGPT', e.target.value)} className={inputCls} placeholder="—" /></td>
                <td className="px-2 py-2 text-center text-xs text-gray-500">U/L</td>
                <td className="px-2 py-2 text-center text-xs text-gray-400">Up to 41</td>
              </tr>
              <tr className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 text-gray-500 text-xs"></td>
                <td className="px-4 py-2 text-gray-600 pl-8">ALP (Alkaline Phosphatase)</td>
                <td className="px-2 py-2"><input value={form.alp || ''} onChange={e => set('alp', e.target.value)} className={inputCls} placeholder="—" /></td>
                <td className="px-2 py-2 text-center text-xs text-gray-500">U/L</td>
                <td className="px-2 py-2 text-center text-xs text-gray-400">40 - 250</td>
              </tr>
              <tr className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 text-gray-500 text-xs"></td>
                <td className="px-4 py-2 text-gray-600 pl-8">Bilirubin (Total)</td>
                <td className="px-2 py-2"><input value={form.bilirubinTotal || ''} onChange={e => set('bilirubinTotal', e.target.value)} className={inputCls} placeholder="—" /></td>
                <td className="px-2 py-2 text-center text-xs text-gray-500">mg/dL</td>
                <td className="px-2 py-2 text-center text-xs text-gray-400">0.1 - 1.2</td>
              </tr>

              {/* Section: RFTs */}
              <tr className="bg-blue-50">
                <td colSpan={5} className="px-4 py-2 text-xs font-bold text-blue-800 uppercase tracking-wide">6. RFTs (Renal Function Test)</td>
              </tr>
              <tr className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 text-gray-500 text-xs"></td>
                <td className="px-4 py-2 text-gray-600 pl-8">Urea</td>
                <td className="px-2 py-2"><input value={form.urea || ''} onChange={e => set('urea', e.target.value)} className={inputCls} placeholder="—" /></td>
                <td className="px-2 py-2 text-center text-xs text-gray-500">mg/dL</td>
                <td className="px-2 py-2 text-center text-xs text-gray-400">10 - 50</td>
              </tr>
              <tr className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 text-gray-500 text-xs"></td>
                <td className="px-4 py-2 text-gray-600 pl-8">Creatinine</td>
                <td className="px-2 py-2"><input value={form.creatinine || ''} onChange={e => set('creatinine', e.target.value)} className={inputCls} placeholder="—" /></td>
                <td className="px-2 py-2 text-center text-xs text-gray-500">mg/dL</td>
                <td className="px-2 py-2 text-center text-xs text-gray-400">0.6 - 1.2</td>
              </tr>
              <tr className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 text-gray-500 text-xs">7.</td>
                <td className="px-4 py-2 text-gray-700">Uric Acid</td>
                <td className="px-2 py-2"><input value={form.uricAcid || ''} onChange={e => set('uricAcid', e.target.value)} className={inputCls} placeholder="—" /></td>
                <td className="px-2 py-2 text-center text-xs text-gray-500">mg/dL</td>
                <td className="px-2 py-2 text-center text-xs text-gray-400">2.5 - 6.0</td>
              </tr>

              {/* Section: Coagulation */}
              <tr className="bg-blue-50">
                <td colSpan={5} className="px-4 py-2 text-xs font-bold text-blue-800 uppercase tracking-wide">III. Coagulation Profile</td>
              </tr>
              <tr className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 text-gray-500 text-xs">8.</td>
                <td className="px-4 py-2 text-gray-700">PT (Prothrombin Time)</td>
                <td className="px-2 py-2"><input value={form.pT_ProthrombinTime || ''} onChange={e => set('pT_ProthrombinTime', e.target.value)} className={inputCls} placeholder="—" /></td>
                <td className="px-2 py-2 text-center text-xs text-gray-500">Sec</td>
                <td className="px-2 py-2 text-center text-xs text-gray-400">12.0 - 14.0</td>
              </tr>
              <tr className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 text-gray-500 text-xs">9.</td>
                <td className="px-4 py-2 text-gray-700">APTT</td>
                <td className="px-2 py-2"><input value={form.aptt || ''} onChange={e => set('aptt', e.target.value)} className={inputCls} placeholder="—" /></td>
                <td className="px-2 py-2 text-center text-xs text-gray-500">Sec</td>
                <td className="px-2 py-2 text-center text-xs text-gray-400">28.0 - 35.0</td>
              </tr>

              {/* Section: Hematology */}
              <tr className="bg-blue-50">
                <td colSpan={5} className="px-4 py-2 text-xs font-bold text-blue-800 uppercase tracking-wide">IV. Hematology</td>
              </tr>
              <tr className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 text-gray-500 text-xs">10.</td>
                <td className="px-4 py-2 text-gray-700">Hemoglobin (Hb)</td>
                <td className="px-2 py-2"><input value={form.hemoglobin || ''} onChange={e => set('hemoglobin', e.target.value)} className={inputCls} placeholder="—" /></td>
                <td className="px-2 py-2 text-center text-xs text-gray-500">g/dL</td>
                <td className="px-2 py-2 text-center text-xs text-gray-400">11.5 - 16.0</td>
              </tr>
              <tr className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 text-gray-500 text-xs">11.</td>
                <td className="px-4 py-2 text-gray-700">Total WBC Count</td>
                <td className="px-2 py-2"><input value={form.totalWBCCount || ''} onChange={e => set('totalWBCCount', e.target.value)} className={inputCls} placeholder="—" /></td>
                <td className="px-2 py-2 text-center text-xs text-gray-500">/cmm</td>
                <td className="px-2 py-2 text-center text-xs text-gray-400">4,000 - 11,000</td>
              </tr>
              <tr className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 text-gray-500 text-xs">12.</td>
                <td className="px-4 py-2 text-gray-700">Platelet Count</td>
                <td className="px-2 py-2"><input value={form.plateletCount || ''} onChange={e => set('plateletCount', e.target.value)} className={inputCls} placeholder="—" /></td>
                <td className="px-2 py-2 text-center text-xs text-gray-500">/cmm</td>
                <td className="px-2 py-2 text-center text-xs text-gray-400">150,000 - 450,000</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-lg transition">Cancel</button>
          <button onClick={handleSubmit} disabled={saving}
            className="px-5 py-2 bg-[#1a2e4a] hover:bg-[#243d61] disabled:opacity-60 text-white text-sm rounded-lg font-medium transition">
            {saving ? 'Saving…' : '💾 Save Report'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── External Lab Panel ───────────────────────────────────────

const PAGE_SIZE = 10;

const ExternalLabPanel = ({ patients }) => {
  const [records,    setRecords]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [modalData,  setModalData]  = useState(null);   // null=closed, false=new, obj=edit
  const [search,     setSearch]     = useState('');
  const [page,       setPage]       = useState(1);
  const [err,        setErr]        = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const data = await api.getAllExternalLabs();
      setRecords(Array.isArray(data) ? data : []);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = records.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.patientName || '').toLowerCase().includes(q) ||
           (r.patientMRNo || '').toLowerCase().includes(q);
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleEdit = async (rec) => {
    // Re-fetch by id to get fresh data
    try {
      const data = await api.getExternalLabById(rec.id, rec.patientId);
      setModalData(Array.isArray(data) ? data[0] : data);
    } catch { setModalData(rec); }
  };

  return (
    <div>
      {err && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">⚠️ {err} <button onClick={load} className="underline ml-2">Retry</button></div>}

      <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
        <h2 className="text-xl font-bold text-gray-900">🧪 External Lab Reports</h2>
        <div className="flex gap-2">
          <input type="text" placeholder="Search by patient or MR..."
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-blue-300" />
          <button onClick={() => setModalData(false)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg font-medium transition">
            + Add Report
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        {loading ? (
          <div className="text-center py-16 text-gray-400"><p className="text-3xl mb-2">⏳</p><p>Loading…</p></div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['#', 'Patient', 'MR No', 'Date', 'HBsAg', 'HCV', 'Hb', 'WBC', 'Actions'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paged.length === 0 ? (
                    <tr><td colSpan={9} className="text-center py-14 text-gray-400">No records found.</td></tr>
                  ) : paged.map(r => (
                    <tr key={r.id} className="hover:bg-gray-50 transition">
                      <td className="px-4 py-3 font-mono text-gray-500 text-xs">#{r.id}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">{r.patientName || `#${r.patientId}`}</td>
                      <td className="px-4 py-3 font-mono text-gray-500 text-xs">{r.patientMRNo || '—'}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{r.testDate || '—'}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{r.hBsAg || '—'}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{r.hcv || '—'}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{r.hemoglobin || '—'}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{r.totalWBCCount || '—'}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => handleEdit(r)}
                          className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded hover:bg-amber-200 font-medium transition">
                          ✏️ Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                <p className="text-xs text-gray-500">Showing {(page-1)*PAGE_SIZE+1}–{Math.min(page*PAGE_SIZE, filtered.length)} of {filtered.length}</p>
                <div className="flex gap-1">
                  <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page===1}
                    className="px-3 py-1 text-xs bg-gray-100 rounded disabled:opacity-40 hover:bg-gray-200">← Prev</button>
                  {Array.from({length: totalPages}, (_, i) => i+1).filter(n => Math.abs(n-page) <= 2).map(n => (
                    <button key={n} onClick={() => setPage(n)}
                      className={`px-3 py-1 text-xs rounded ${n===page ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}>{n}</button>
                  ))}
                  <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page===totalPages}
                    className="px-3 py-1 text-xs bg-gray-100 rounded disabled:opacity-40 hover:bg-gray-200">Next →</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {modalData !== null && (
        <ExternalLabModal
          initial={modalData || null}
          patients={patients}
          onClose={() => setModalData(null)}
          onSaved={() => { setModalData(null); load(); }}
        />
      )}
    </div>
  );
};
const EMPTY_FORM = { patientId: '', testId: '', notes: '' };

  const LabPanel = ({ user, onLogout }) => {
  const [activeTab,  setActiveTab]  = useState('internal'); // 'internal' | 'external'
  const [patients,   setPatients]   = useState([]);
  const [reports,    setReports]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm,   setShowForm]   = useState(false);
  const [search,     setSearch]     = useState('');
  const [pageError,  setPageError]  = useState('');
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [fileObj,    setFileObj]    = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setPageError('');
    try {
      const [patientList, reportList] = await Promise.all([
        api.getPatients(),
        api.getAllReports(),
        api.getAllExternalLabs(), // prefetch external on login — result not stored here, ExternalLabPanel manages its own state
      ]);
      setPatients(Array.isArray(patientList) ? patientList : []);
      setReports(Array.isArray(reportList)   ? reportList  : []);
    } catch (e) {
      setPageError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.type !== 'application/pdf') {
      alert('PDF files only.');
      e.target.value = '';
      return;
    }
    setFileObj(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.patientId)    { alert('Select a patient.');  return; }
    if (form.testId === '')  { alert('Select a test.');    return; }
    if (!fileObj)            { alert('Upload a PDF file.'); return; }

    setSubmitting(true);
    try {
      // Read PDF → base64
      const base64 = await new Promise((res, rej) => {
        const reader   = new FileReader();
        reader.onload  = (ev) => res(ev.target.result.split(',')[1]);
        reader.onerror = rej;
        reader.readAsDataURL(fileObj);
      });

      await api.saveReport({
        id:         0,
        patientId:  Number(form.patientId),
        testId:     Number(form.testId),
        reportFile: base64,
        notes:      form.notes,
      });

      alert('Lab report uploaded successfully!');
      setForm(EMPTY_FORM);
      setFileObj(null);
      const inp = document.getElementById('pdf-input');
      if (inp) inp.value = '';
      setShowForm(false);
      await loadData();
    } catch (err) {
      alert('Upload failed: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getPatient     = (id) => patients.find(p => String(p.id) === String(id));
  const getPatientName = (id) => { const p = getPatient(id); return p ? (p.name || p.patientName || `#${id}`) : `#${id}`; };
  const getPatientMR   = (id) => { const p = getPatient(id); return p?.mrNo || p?.mrNumber || ''; };

  const filtered = reports.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      getPatientName(r.patientId).toLowerCase().includes(q) ||
      getPatientMR(r.patientId).toLowerCase().includes(q)   ||
      getTestName(r.testId).toLowerCase().includes(q)
    );
  });

  const today = new Date().toLocaleDateString('en-GB');

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="" className="w-10 h-10" onError={e => e.target.style.display='none'} />
            <div>
              <h1 className="font-bold text-gray-900">Lab Panel</h1>
              <p className="text-xs text-gray-500">Upload & Manage Lab Reports</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">🔬 {user?.name}</span>
            <button onClick={onLogout} className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition">
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">

        {pageError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex justify-between">
            <span>⚠️ {pageError}</span>
            <button onClick={loadData} className="underline ml-3">Retry</button>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="rounded-xl p-4 bg-purple-50 border border-purple-200">
            <p className="text-purple-600 text-xs font-medium">Total Reports</p>
            <p className="text-2xl font-bold text-purple-700">{reports.length}</p>
          </div>
          <div className="rounded-xl p-4 bg-blue-50 border border-blue-200">
            <p className="text-blue-600 text-xs font-medium">Total Patients</p>
            <p className="text-2xl font-bold text-blue-700">{patients.length}</p>
          </div>
          <div className="rounded-xl p-4 bg-green-50 border border-green-200">
            <p className="text-green-600 text-xs font-medium">Unique w/ Reports</p>
            <p className="text-2xl font-bold text-green-700">{new Set(reports.map(r => r.patientId)).size}</p>
          </div>
          <div className="rounded-xl p-4 bg-white border border-gray-100">
            <p className="text-gray-500 text-sm">Today</p>
            <p className="text-lg font-bold text-gray-700">{today}</p>
          </div>
        </div>

        {/* ── Tab Switcher ── */}
        <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit">
          <button onClick={() => setActiveTab('internal')}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition ${activeTab === 'internal' ? 'bg-white shadow text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}>
            🏥 Internal Lab
          </button>
          <button onClick={() => setActiveTab('external')}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition ${activeTab === 'external' ? 'bg-white shadow text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}>
            🧪 External Lab
          </button>
        </div>

        {/* ── Internal Tab ── */}
        {activeTab === 'internal' && (
          <>
            <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
              <h2 className="text-xl font-bold text-gray-900">🔬 Internal Lab Reports</h2>
              <div className="flex gap-2">
                <input type="text" placeholder="Search by patient or test..."
                  value={search} onChange={e => setSearch(e.target.value)}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-300" />
                <button onClick={() => setShowForm(v => !v)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg font-medium transition">
                  + Upload Report
                </button>
              </div>
            </div>

            {showForm && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 shadow-sm">
                <h3 className="font-bold text-gray-900 mb-4">Upload Lab Report</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Select Patient *</label>
                      <select value={form.patientId}
                        onChange={e => setForm({ ...form, patientId: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" required>
                        <option value="">-- Select Patient --</option>
                        {patients.map(p => (
                          <option key={p.id} value={p.id}>
                            {p.name || p.patientName}{p.mrNo ? ` (${p.mrNo})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Test Name *</label>
                      <select value={form.testId}
                        onChange={e => setForm({ ...form, testId: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" required>
                        <option value="">-- Select Test --</option>
                        {TEST_TYPES.map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Upload Report (PDF Only) *</label>
                    <input id="pdf-input" type="file" accept="application/pdf" onChange={handleFileChange}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
                    {fileObj
                      ? <p className="text-xs text-green-600 mt-1">✅ {fileObj.name} ({(fileObj.size/1024).toFixed(1)} KB)</p>
                      : <p className="text-xs text-gray-400 mt-1">No file chosen</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Notes (Optional)</label>
                    <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" rows={2}
                      placeholder="Any additional notes..." />
                  </div>

                  <div className="flex gap-2">
                    <button type="submit" disabled={submitting}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm rounded-lg font-medium transition">
                      {submitting ? 'Uploading…' : 'Upload Report'}
                    </button>
                    <button type="button"
                      onClick={() => { setShowForm(false); setForm(EMPTY_FORM); setFileObj(null); }}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-lg transition">
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              {loading ? (
                <div className="text-center py-16 text-gray-400">
                  <p className="text-3xl mb-2">⏳</p>
                  <p>Loading reports…</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        {['#', 'Patient', 'Name', 'Test', 'Notes', 'Actions'].map(h => (
                          <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filtered.length === 0 ? (
                        <tr><td colSpan={6} className="text-center py-14 text-gray-400">No reports found.</td></tr>
                      ) : filtered.map(r => (
                        <tr key={r.id} className="hover:bg-gray-50 transition">
                          <td className="px-4 py-3 font-mono text-gray-500 text-xs">#{r.id}</td>
                          <td className="px-4 py-3 font-medium text-gray-800">{getPatientName(r.patientId)}</td>
<td className="px-4 py-3 font-mono text-gray-500 text-xs">{r.mrNo || getPatientMR(r.patientId) || '—'}</td>
                          <td className="px-4 py-3 text-gray-700">{getTestName(r.testId)}</td>
                          <td className="px-4 py-3 text-gray-500 max-w-[160px] truncate" title={r.notes}>{r.notes || '—'}</td>
                          <td className="px-4 py-3">
                            <ReportActions report={r} patientMR={getPatientMR(r.patientId)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* ── External Tab ── */}
        {activeTab === 'external' && (
          <ExternalLabPanel patients={patients} />
        )}

      </div>
    </div>
  );
};

export default LabPanel;